import { useState } from "react";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import useUsers from "@/hooks/useUsers";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import type { LegislationMetadataPreview } from "@/components/modals/LegislationSubscribeModal";

export type SubscribeActionParams = {
  subscribeCategory: SubscriptionCategory;
  entityPath: string;
  entityName: string;
  source: string;
  isSubscribed: boolean;
  legislationExternalId?: string;
  legislationDatasource?: string;
  legislationSubscriptionId?: string;
  legislationMetadata?: LegislationMetadataPreview;
};

export type SubscribeActionMutations = {
  subscribe: (request: {
    subscribeType: SubscriptionCategory;
    source: string;
    entityPath: string;
    entityName: string;
  }) => Promise<unknown>;
  unsubscribe: (request: {
    subscribeType: SubscriptionCategory;
    source: string;
    entityPath: string;
    entityName: string;
  }) => Promise<unknown>;
  createLegislationSubscription: (request: {
    externalId: string;
    datasource: string;
    name: string;
    timelineUpdates?: boolean;
    latestExternalId?: string;
  }) => Promise<unknown>;
  removeLegislationSubscription: (id: string) => Promise<unknown>;
};

export async function executeDefaultSubscribeToggle(
  params: Pick<
    SubscribeActionParams,
    | "subscribeCategory"
    | "entityPath"
    | "entityName"
    | "source"
    | "isSubscribed"
  >,
  mutations: Pick<SubscribeActionMutations, "subscribe" | "unsubscribe">
): Promise<void> {
  const request = {
    subscribeType: params.subscribeCategory,
    source: params.source,
    entityPath: params.entityPath,
    entityName: params.entityName,
  };
  if (params.isSubscribed) {
    await mutations.unsubscribe(request);
  } else {
    await mutations.subscribe(request);
  }
}

export async function executeLegislationUnsubscribe(
  legislationSubscriptionId: string,
  mutations: Pick<SubscribeActionMutations, "removeLegislationSubscription">
): Promise<void> {
  await mutations.removeLegislationSubscription(legislationSubscriptionId);
}

export async function executeLegislationSubscribeCreate(
  params: {
    legislationExternalId: string;
    legislationDatasource: string;
    entityName: string;
    subscriptionName: string;
    includeTimelineUpdates: boolean;
    includeLatest: boolean;
  },
  mutations: Pick<SubscribeActionMutations, "createLegislationSubscription">
): Promise<void> {
  await mutations.createLegislationSubscription({
    externalId: params.legislationExternalId,
    datasource: params.legislationDatasource,
    name: params.subscriptionName.trim() || params.entityName,
    timelineUpdates: params.includeTimelineUpdates,
    latestExternalId: params.includeLatest
      ? params.legislationExternalId
      : undefined,
  });
}

type UseSubscribeActionOptions = SubscribeActionParams & {
  onOperationStart?: () => void;
  onOperationEnd?: () => void;
};

export function useSubscribeAction({
  onOperationStart,
  onOperationEnd,
  ...params
}: UseSubscribeActionOptions) {
  const {
    subscribe,
    unsubscribe,
    createLegislationSubscription,
    removeLegislationSubscription,
    subscribeLoading,
  } = useUsers();

  const isLegislation =
    params.subscribeCategory === SubscriptionCategory.Legislation;

  const pendingKey =
    isLegislation && params.isSubscribed && params.legislationSubscriptionId
      ? `legislation-${params.legislationSubscriptionId}`
      : params.entityPath;

  const [isMutating, setIsMutating] = useState(false);
  const [isLegislationModalOpen, setIsLegislationModalOpen] = useState(false);

  // Subscribe so store pending updates re-render (getState() alone does not).
  const isStorePending = useSubscriptionOperationStore((s) =>
    s.pendingOperations.has(pendingKey)
  );

  const isLoading =
    isMutating || isStorePending || subscribeLoading(pendingKey);
  const label = params.isSubscribed ? "Fjern abonnering" : "Abonner";

  const mutations: SubscribeActionMutations = {
    subscribe: (request) => subscribe.mutateAsync(request),
    unsubscribe: (request) => unsubscribe.mutateAsync(request),
    createLegislationSubscription: (request) =>
      createLegislationSubscription.mutateAsync(request),
    removeLegislationSubscription: (id) =>
      removeLegislationSubscription.mutateAsync(id),
  };

  /** Opens create modal for legislation; toggles for everything else. */
  const activate = async () => {
    if (isLegislation && !params.isSubscribed) {
      setIsLegislationModalOpen(true);
      return;
    }

    if (isLegislation && !params.legislationSubscriptionId) {
      return;
    }

    setIsMutating(true);
    onOperationStart?.();
    try {
      if (isLegislation) {
        await executeLegislationUnsubscribe(
          params.legislationSubscriptionId!,
          mutations
        );
      } else {
        await executeDefaultSubscribeToggle(params, mutations);
      }
    } catch (error) {
      logger.error("Failed to update subscription", {
        operation: "toggleSubscription",
        error,
        subscribeCategory: params.subscribeCategory,
        entityPath: params.entityPath,
        isSubscribed: params.isSubscribed,
      });
      showErrorToast(
        params.isSubscribed
          ? "Kunne ikke fjerne abonnementet. Prøv igen senere."
          : "Kunne ikke oprette abonnement. Prøv igen senere."
      );
    } finally {
      setIsMutating(false);
      onOperationEnd?.();
    }
  };

  const confirmLegislationSubscribe = async (options: {
    subscriptionName: string;
    includeTimelineUpdates: boolean;
    includeLatest: boolean;
  }) => {
    if (
      params.legislationExternalId === undefined ||
      params.legislationDatasource === undefined
    ) {
      return;
    }

    setIsMutating(true);
    onOperationStart?.();
    try {
      await executeLegislationSubscribeCreate(
        {
          legislationExternalId: params.legislationExternalId,
          legislationDatasource: params.legislationDatasource,
          entityName: params.entityName,
          subscriptionName: options.subscriptionName,
          includeTimelineUpdates: options.includeTimelineUpdates,
          includeLatest: options.includeLatest,
        },
        mutations
      );
      setIsLegislationModalOpen(false);
    } catch (error) {
      logger.error("Failed to create legislation subscription", {
        operation: "createLegislationSubscription",
        error,
        entityPath: params.entityPath,
        legislationExternalId: params.legislationExternalId,
        legislationDatasource: params.legislationDatasource,
      });
      showErrorToast("Kunne ikke oprette abonnement. Prøv igen senere.");
    } finally {
      setIsMutating(false);
      onOperationEnd?.();
    }
  };

  return {
    activate,
    isLoading,
    label,
    isLegislationModalOpen,
    setIsLegislationModalOpen,
    confirmLegislationSubscribe,
    isLegislation,
  };
}

export default useSubscribeAction;
